<template>
  <div v-if="!dismissed" class="modal-shadow saved_popup min-h-screen flex flex-col">
    <div class="inner">
      <div class="content">
        <div class="w-full flex justify-between modal_header p-6 title_line">
          <div class="modal_title mt-2">
            {{ phase === 'importing' ? 'Importing routes' : 'Import complete' }}
          </div>
          <button
            v-if="phase !== 'importing'"
            class="x_btn"
            @click="closeModal"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
          </button>
        </div>

        <div class="modal_body flex flex-col mt-4 px-6 pb-4">
          <p class="text-left mb-3 text-sm">
            Importing route {{ current }} of {{ total }}…
            <span v-if="phase !== 'importing'">
              — <strong>Created:</strong> {{ summary.created }}
              &nbsp;|&nbsp; <strong>Failed:</strong> {{ summary.failed }}
            </span>
          </p>
          <div class="w-full bg-gray-200 rounded h-3 mb-4 overflow-hidden">
            <div
              class="bg-green-custom h-3 rounded transition-all"
              :style="{ width: progressPercent + '%' }"
            />
          </div>

          <div class="grid-wrap border rounded overflow-auto max-h-96">
            <table class="w-full text-xs import-grid">
              <thead>
                <tr class="border-b bg-gray-50 sticky top-0">
                  <th class="p-2 text-left row-num-col">#</th>
                  <th class="p-2 text-left status-col">Status</th>
                  <th class="p-2 text-left error-col">Error</th>
                  <th
                    v-for="header in displayHeaders"
                    :key="'h-' + header"
                    class="p-2 text-left whitespace-nowrap"
                  >
                    {{ header }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="line in rowLines"
                  :key="'row-' + line.rowIndex"
                  class="border-b"
                  :class="rowClass(line)"
                  :title="line.message || ''"
                >
                  <td class="p-2 text-left row-num-col">{{ line.rowIndex }}</td>
                  <td class="p-2 text-left status-col">
                    <span v-if="line.status === 'pending'" class="text-grey-dark">…</span>
                    <span v-else-if="line.status === 'ok'" class="text-green-700 font-semibold">OK</span>
                    <span v-else class="text-red-700 font-semibold">Fail</span>
                  </td>
                  <td
                    class="p-2 text-left error-col text-red-800 whitespace-normal"
                    :title="line.message || ''"
                  >
                    <span v-if="line.status === 'error'">{{ line.message }}</span>
                  </td>
                  <td
                    v-for="header in displayHeaders"
                    :key="'c-' + line.rowIndex + header"
                    class="p-2 text-left max-w-xs truncate"
                    :class="cellClass(header, line)"
                  >
                    {{ cellDisplay(line, header) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <p v-if="phase !== 'importing' && summary.successes.length" class="text-left mt-4 text-sm">
            <span class="font-bold">Created routes:</span>
            <span v-for="(item, i) in summary.successes" :key="'ok-' + item.rowIndex">
              <template v-if="i > 0">; </template>
              Row {{ item.rowIndex }}:
              <a
                :href="'/routes-v2/' + item.routeId"
                target="_blank"
                rel="noopener"
                class="text-primary underline"
              >{{ item.routeName }}</a>
            </span>
          </p>
        </div>

        <div
          v-if="phase !== 'importing'"
          class="btn_line flex justify-end w-full px-6 py-4 border-t modal_footer"
        >
          <button
            type="button"
            @click="closeModal"
            class="this_button py-3 px-4 border rounded bg-white hover:bg-gray-100"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  validateRoutePlannerImportRow,
} from '@/utils/routesRoutePlannerCsv';
import {
  cellValueForHeader,
  headerMatchesErrorField,
  resolveRoutesImportErrorField,
} from '@/utils/routesImportErrorField';

export default {
  name: 'RoutesCsvImportModal',
  props: {
    rows: {
      type: Array,
      required: true,
    },
    displayHeaders: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      dismissed: false,
      phase: 'importing',
      current: 0,
      total: 0,
      rowLines: [],
      summary: {
        created: 0,
        failed: 0,
        failures: [],
        successes: [],
      },
    };
  },
  computed: {
    progressPercent() {
      if (!this.total) {
        return 0;
      }
      return Math.round((this.current / this.total) * 100);
    },
  },
  async mounted() {
    this.total = this.rows.length;
    this.rowLines = this.rows.map((entry) => {
      const rowIndex =
        entry?.blockStartRowIndex != null ? entry.blockStartRowIndex : 0;
      return {
        rowIndex,
        row: entry?.row != null ? entry.row : entry,
        status: 'pending',
        message: '',
        errorField: null,
        routeId: null,
        routeName: '',
      };
    });
    await this.runImport();
  },
  methods: {
    closeModal() {
      this.dismissed = true;
      this.$emit('finished');
    },
    rowClass(line) {
      if (line.status === 'ok') {
        return 'row-ok';
      }
      if (line.status === 'error') {
        return 'row-fail';
      }
      return 'row-pending';
    },
    cellClass(header, line) {
      if (line.status !== 'error' || !line.errorField) {
        return '';
      }
      return headerMatchesErrorField(header, line.errorField)
        ? 'cell-error'
        : '';
    },
    cellDisplay(line, header) {
      return cellValueForHeader(line.row, header);
    },
    applyLineResult(line, result, clientMissing) {
      if (result?.status === 'created') {
        line.status = 'ok';
        line.message = '';
        line.errorField = null;
        line.routeId = result.routeId;
        line.routeName = result.routeName || '';
        return;
      }
      line.status = 'error';
      line.message = result?.message || 'Import failed';
      line.errorField = resolveRoutesImportErrorField(result, clientMissing);
      line.routeName = result?.routeName || '';
    },
    async runImport() {
      let localCreated = 0;
      let localFailed = 0;
      const failures = [];
      const successes = [];

      try {
        const sessionRes = await this.$http.postAuth(
          `${this.$http.apiUrl()}/routes/import-csv-v2/session`,
          { totalRows: this.total },
        );
        const sessionId = sessionRes?.data?.sessionId;
        if (!sessionId) {
          throw new Error('Could not start import session');
        }

        for (let i = 0; i < this.rows.length; i++) {
          this.current = i + 1;
          const entry = this.rows[i];
          const line = this.rowLines[i];
          const row = entry?.row != null ? entry.row : entry;
          const rowIndex =
            entry?.blockStartRowIndex != null ? entry.blockStartRowIndex : i + 1;
          const clientMissing = validateRoutePlannerImportRow(row);

          if (clientMissing.length) {
            const result = {
              status: 'failed',
              message: `Missing required column(s): ${clientMissing.join(', ')}`,
            };
            this.applyLineResult(line, result, clientMissing);
            localFailed += 1;
            failures.push({
              rowIndex,
              routeName: row['route name'] || '',
              message: line.message,
              errorField: line.errorField,
            });
            continue;
          }

          let result;
          try {
            const res = await this.$http.postAuth(
              `${this.$http.apiUrl()}/routes/import-csv-v2/row`,
              {
                sessionId,
                rowIndex,
                row,
              },
            );
            result = res?.data || res;
          } catch (err) {
            const body = err?.response?.data;
            result = {
              status: 'failed',
              message:
                (typeof body?.message === 'string' && body.message) ||
                (Array.isArray(body?.message) && body.message.join(', ')) ||
                'Import failed',
              errorField: body?.errorField,
            };
          }
          this.applyLineResult(line, result, clientMissing);

          if (result?.status === 'created') {
            localCreated += 1;
            successes.push({
              rowIndex,
              routeId: result.routeId,
              routeName: result.routeName,
            });
          } else {
            localFailed += 1;
            failures.push({
              rowIndex,
              routeName: result?.routeName || '',
              message: line.message || result?.message || 'Failed',
              errorField: line.errorField,
            });
          }
        }

        await this.$http.postAuth(
          `${this.$http.apiUrl()}/routes/import-csv-v2/session/complete`,
          { sessionId },
        );

        this.summary = {
          created: localCreated,
          failed: localFailed,
          failures,
          successes,
        };
      } catch (e) {
        console.error(e);
        this.rowLines.forEach((line) => {
          if (line.status === 'pending') {
            line.status = 'error';
            line.message = 'Import failed unexpectedly';
          }
        });
        this.summary = {
          created: localCreated,
          failed: this.total - localCreated,
          failures: [
            {
              rowIndex: 0,
              routeName: '',
              message: 'Import failed unexpectedly',
            },
          ],
          successes,
        };
      }
      this.phase = 'complete';
    },
  },
};
</script>

<style lang="scss" scoped>
.modal-shadow {
  position: fixed;
  top: 0;
  left: 0;
  min-height: 100%;
  width: 100%;
  background: rgba(0, 0, 0, 0.39);
  z-index: 403;
}
.modal_header {
  border-radius: 8px 8px 0 0;
  border-bottom: none;
}
.modal_footer {
  border-radius: 0 0 8px 8px;
}
.btn_line {
  border-radius: 0 0 8px 8px;
}
.this_button {
  font-weight: 600;
  transition: all 0.2s ease;
  min-width: 5rem;
}
.saved_popup {
  position: fixed;
  right: 0;
  top: 0;
  bottom: 0;
  width: 100vw;
}
.inner {
  margin: 24px auto;
  max-width: min(1200px, 96vw);
  background: #fff;
  border-radius: 8px;
}
.row-ok {
  background-color: #ecfdf3;
}
.row-fail {
  background-color: #fef2f2;
}
.row-pending {
  background-color: #fff;
}
.cell-error {
  background-color: #fecaca;
  box-shadow: inset 0 0 0 1px #ef4444;
}
.row-num-col {
  width: 2.5rem;
}
.status-col {
  width: 3rem;
}
.error-col {
  min-width: 10rem;
  max-width: 18rem;
}
.import-grid th,
.import-grid td {
  text-align: left;
  vertical-align: top;
}
</style>
